import {
	CLAUSE_TYPE_COMPOUND,
	CLAUSE_TYPE_TERMINAL,
	COMPOUND_OPERATOR_AND,
	COMPOUND_OPERATOR_OR,
	type Jast,
} from '@atlaskit/jql-ast';
import type { TextSearchInputClauseType } from '@atlassian/jira-jql-utils/src/utils/text-search-input-clause/types.tsx';
import { validate } from './validate.tsx';

type Result = {
	isValid: boolean;
	textSearchInputClause: TextSearchInputClauseType;
};

/**
 * getTextSearchInputClause fetches the text search input clause from the JQL AST and checks if it is valid
 * We don't use a visitor and access query directly because we only care if there is a text search input clause at root level
 */
export const getTextSearchInputClause = (ast: Jast, textSearchInputJqlTerm: string): Result => {
	let textSearchInputFound = false;
	const whereClause = ast?.query?.where;

	// If there is no clause, we consider the query valid
	if (!whereClause) {
		return { isValid: true, textSearchInputClause: undefined };
	}

	// Only 1 terminal clause at top level
	if (whereClause.clauseType === CLAUSE_TYPE_TERMINAL) {
		// Check validity if it's the text search input clause
		if (whereClause.field.value.toLowerCase() === textSearchInputJqlTerm.toLowerCase()) {
			return { isValid: validate(whereClause), textSearchInputClause: whereClause };
		}

		// If it's not the text search input clause, we consider the JQL valid
		return { isValid: true, textSearchInputClause: undefined };
	}

	if (whereClause.clauseType === CLAUSE_TYPE_COMPOUND) {
		const compoundClause = whereClause;

		// We only support OR if the compound clause is a text search input clause
		if (compoundClause.operator.value === COMPOUND_OPERATOR_OR) {
			if (
				compoundClause.clauses.some(
					(c) =>
						c.clauseType === CLAUSE_TYPE_TERMINAL &&
						c.field.value.toLowerCase() === textSearchInputJqlTerm.toLowerCase(),
				)
			) {
				return { isValid: validate(compoundClause), textSearchInputClause: compoundClause };
			}

			// If there is no text search input clause at root level, we consider the JQL valid
			return { isValid: true, textSearchInputClause: undefined };
		}

		if (compoundClause.operator.value === COMPOUND_OPERATOR_AND) {
			let textSearchInputClause: TextSearchInputClauseType;
			let isValid = true;
			for (const clause of compoundClause.clauses) {
				if (clause.clauseType === CLAUSE_TYPE_TERMINAL) {
					if (clause.field.value.toLowerCase() === textSearchInputJqlTerm.toLowerCase()) {
						if (textSearchInputFound) {
							isValid = false;
							textSearchInputClause = undefined;
						} else {
							textSearchInputFound = true;
							isValid = validate(clause);
							textSearchInputClause = clause;
						}
					}
				}

				if (clause.clauseType === CLAUSE_TYPE_COMPOUND) {
					// If it's compound clause could be a text search input clause only if has OR operator (eg textfields ~ "EM-123" OR issuekey = "EM-123")
					if (
						clause.clauses.some(
							(c) =>
								c.clauseType === CLAUSE_TYPE_TERMINAL &&
								c.field.value.toLowerCase() === textSearchInputJqlTerm.toLowerCase(),
						) &&
						clause.operator.value === COMPOUND_OPERATOR_OR
					) {
						if (textSearchInputFound) {
							isValid = false;
							textSearchInputClause = undefined;
						} else {
							textSearchInputClause = clause;
							isValid = validate(clause);

							textSearchInputFound = true;
						}
					}
				}
			}
			return { isValid, textSearchInputClause };
		}

		return { isValid: false, textSearchInputClause: undefined };
	}
	return { isValid: false, textSearchInputClause: undefined };
};
